import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { 
  X, 
  Target,
  Users,
  ChevronRight,
  ChevronDown,
  AlertTriangle,
  Sparkles,
  Download,
  Search,
  Zap,
  BarChart3,
  ListChecks,
  Loader2,
  TrendingUp
} from 'lucide-react';
import { Task } from '../shared/types';
import { api, ResourcePlanResult, WeeklyResourceData } from '../../lib/api';

interface ResourcePlannerModalProps {
  isOpen: boolean;
  onClose: () => void;
  tasks: Task[];
  projectId: string;
  projectName?: string;
  onCreatePlan?: (tasks: Task[], planType: 'monthly' | 'weekly') => void;
}

type PlanningMode = 'task-first' | 'resource-first';

interface ResourceLimit {
  labor: string;
  available: number;
  color: string;
}

// Labor type colors
const LABOR_COLORS: Record<string, string> = {
  'Mason': '#3B82F6',
  'Helper': '#22C55E',
  'Carpenter': '#F59E0B',
  'Plumber': '#06B6D4',
  'Electrician': '#EAB308',
  'Painter': '#EC4899',
  'Tiler': '#8B5CF6',
  'Welder': '#EF4444',
  'Fitter': '#6366F1',
};

const getLaborColor = (labor: string): string => {
  return LABOR_COLORS[labor] || '#6B7280';
};

// Resource Line Chart Component
const ResourceLineChart: React.FC<{
  weeks: WeeklyResourceData[];
  totals: Record<string, { total: number; peak: number; peakWeek: number }>;
  resourceLimits: ResourceLimit[];
}> = ({ weeks, totals, resourceLimits }) => {
  const laborTypes = Object.keys(totals);
  const maxValue = Math.max(
    ...weeks.flatMap(w => Object.values(w.resources)),
    ...resourceLimits.map(r => r.available),
    10
  );
  
  const chartWidth = Math.max(weeks.length * 60, 400);
  const chartHeight = 120;
  const padding = { top: 10, right: 20, bottom: 30, left: 40 };
  const innerWidth = chartWidth - padding.left - padding.right;
  const innerHeight = chartHeight - padding.top - padding.bottom;

  // Generate path for each labor type
  const generatePath = (labor: string) => {
    const points = weeks.map((week, i) => {
      const x = padding.left + (i / Math.max(weeks.length - 1, 1)) * innerWidth;
      const y = padding.top + innerHeight - ((week.resources[labor] || 0) / maxValue) * innerHeight;
      return `${i === 0 ? 'M' : 'L'} ${x} ${y}`;
    });
    return points.join(' ');
  };

  return (
    <div className="overflow-x-auto">
      <svg width={chartWidth} height={chartHeight} className="min-w-full">
        {/* Grid lines */}
        {[0, 0.25, 0.5, 0.75, 1].map((ratio, i) => (
          <g key={i}>
            <line
              x1={padding.left}
              y1={padding.top + innerHeight * (1 - ratio)}
              x2={chartWidth - padding.right}
              y2={padding.top + innerHeight * (1 - ratio)}
              stroke="#E5E7EB"
              strokeDasharray="4"
            />
            <text
              x={padding.left - 5}
              y={padding.top + innerHeight * (1 - ratio) + 4}
              textAnchor="end"
              className="text-xs fill-gray-400"
            >
              {Math.round(maxValue * ratio)}
            </text>
          </g>
        ))}

        {/* Resource limit lines */}
        {resourceLimits.map(limit => {
          const y = padding.top + innerHeight - (limit.available / maxValue) * innerHeight;
          return (
            <line
              key={`limit-${limit.labor}`}
              x1={padding.left}
              y1={y}
              x2={chartWidth - padding.right}
              y2={y}
              stroke={limit.color}
              strokeDasharray="6,3"
              strokeWidth={1}
              opacity={0.5}
            />
          );
        })}

        {/* Lines for each labor type */}
        {laborTypes.map(labor => (
          <path
            key={labor}
            d={generatePath(labor)}
            fill="none"
            stroke={getLaborColor(labor)}
            strokeWidth={2}
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        ))}

        {/* Data points */}
        {laborTypes.map(labor => (
          weeks.map((week, i) => {
            const x = padding.left + (i / Math.max(weeks.length - 1, 1)) * innerWidth;
            const y = padding.top + innerHeight - ((week.resources[labor] || 0) / maxValue) * innerHeight;
            const limit = resourceLimits.find(r => r.labor === labor)?.available || Infinity;
            const isOverLimit = (week.resources[labor] || 0) > limit;
            
            return (
              <circle
                key={`${labor}-${i}`}
                cx={x}
                cy={y}
                r={isOverLimit ? 5 : 3}
                fill={isOverLimit ? '#EF4444' : getLaborColor(labor)}
                stroke="white"
                strokeWidth={1}
              >
                <title>{`${labor}: ${week.resources[labor] || 0} (Week ${week.week})`}</title>
              </circle>
            );
          })
        ))}

        {/* Week labels */}
        {weeks.map((week, i) => {
          const x = padding.left + (i / Math.max(weeks.length - 1, 1)) * innerWidth;
          return (
            <text
              key={i}
              x={x}
              y={chartHeight - 5}
              textAnchor="middle"
              className="text-xs fill-gray-500"
            >
              W{week.week}
            </text>
          );
        })}
      </svg>
    </div>
  );
};

// Compact Weekly Calendar Component
const WeeklyCalendarGrid: React.FC<{
  weeks: (WeeklyResourceData & { overLimit: string[] })[];
  totals: Record<string, { total: number; peak: number; peakWeek: number }>;
  resourceLimits: ResourceLimit[];
}> = ({ weeks, totals, resourceLimits }) => {
  const laborTypes = Object.keys(totals);
  const maxValue = Math.max(...weeks.flatMap(w => Object.values(w.resources)), 1);

  return (
    <div className="overflow-x-auto">
      <div className="inline-flex gap-1 min-w-full pb-2">
        {weeks.map(week => {
          const hasOverLimit = week.overLimit.length > 0;
          const startDate = new Date(week.startDate);
          const endDate = new Date(week.endDate);
          
          return (
            <div
              key={week.week}
              className={`flex-shrink-0 w-24 rounded-lg border ${
                hasOverLimit ? 'border-red-300 bg-red-50' : 'border-gray-200 bg-white'
              }`}
            >
              {/* Week Header */}
              <div className={`px-2 py-1 text-center border-b ${
                hasOverLimit ? 'border-red-200 bg-red-100' : 'border-gray-100 bg-gray-50'
              }`}>
                <div className="text-xs font-semibold text-gray-700">W{week.week}</div>
                <div className="text-[10px] text-gray-500">
                  {startDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                </div>
              </div>
              
              {/* Resource Bars */}
              <div className="p-1.5 space-y-1">
                {laborTypes.slice(0, 5).map(labor => {
                  const count = week.resources[labor] || 0;
                  const limit = resourceLimits.find(r => r.labor === labor)?.available || Infinity;
                  const isOver = count > limit;
                  const barWidth = Math.min((count / maxValue) * 100, 100);
                  
                  return (
                    <div key={labor} className="flex items-center gap-1">
                      <div 
                        className="w-2 h-2 rounded-full flex-shrink-0"
                        style={{ backgroundColor: getLaborColor(labor) }}
                      />
                      <div className="flex-1 h-3 bg-gray-100 rounded overflow-hidden">
                        <div
                          className={`h-full rounded ${isOver ? 'bg-red-500' : ''}`}
                          style={{ 
                            width: `${barWidth}%`,
                            backgroundColor: isOver ? undefined : getLaborColor(labor),
                            opacity: 0.8
                          }}
                        />
                      </div>
                      <span className={`text-[10px] w-4 text-right ${isOver ? 'text-red-600 font-bold' : 'text-gray-600'}`}>
                        {count}
                      </span>
                    </div>
                  );
                })}
              </div>
              
              {/* Task Count */}
              <div className="px-2 py-1 text-center border-t border-gray-100">
                <span className="text-[10px] text-gray-500">{week.taskCount} tasks</span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export const ResourcePlannerModal: React.FC<ResourcePlannerModalProps> = ({
  isOpen,
  onClose,
  tasks,
  projectId,
  projectName = 'Project',
  onCreatePlan
}) => {
  // Mode selection
  const [mode, setMode] = useState<PlanningMode>('task-first');
  
  // Task-First Mode state
  const [selectedTaskIds, setSelectedTaskIds] = useState<Set<string>>(new Set());
  const [taskSearchQuery, setTaskSearchQuery] = useState('');
  const [taskFilterCategory, setTaskFilterCategory] = useState<string>('');
  const [taskFilterBuilding, setTaskFilterBuilding] = useState<string>('');
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());
  const [groupBy, setGroupBy] = useState<'category' | 'floor' | 'building'>('floor');
  
  // Resource-First Mode state
  const [resourceLimits, setResourceLimits] = useState<ResourceLimit[]>([
    { labor: 'Mason', available: 20, color: LABOR_COLORS['Mason'] },
    { labor: 'Helper', available: 30, color: LABOR_COLORS['Helper'] },
    { labor: 'Carpenter', available: 10, color: LABOR_COLORS['Carpenter'] },
    { labor: 'Plumber', available: 8, color: LABOR_COLORS['Plumber'] },
    { labor: 'Electrician', available: 8, color: LABOR_COLORS['Electrician'] },
  ]);
  const [suggestedTaskIds, setSuggestedTaskIds] = useState<Set<string>>(new Set());

  // API response state
  const [resourcePlan, setResourcePlan] = useState<ResourcePlanResult | null>(null);
  const [isCalculating, setIsCalculating] = useState(false);
  const [isSuggesting, setIsSuggesting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Get unique categories, buildings, and floors for filters
  const { categories, buildings, floors } = useMemo(() => {
    const cats = new Set<string>();
    const bldgs = new Set<string>();
    const flrs = new Set<string>();
    tasks.forEach(t => {
      if (t.category) cats.add(t.category);
      if (t.building) bldgs.add(t.building);
      if (t.floor) flrs.add(t.floor);
    });
    return { 
      categories: Array.from(cats).sort(), 
      buildings: Array.from(bldgs).sort(),
      floors: Array.from(flrs).sort((a, b) => {
        // Sort floors naturally (Plinth, Ground, 1st, 2nd, etc.)
        const order = ['Plinth', 'Ground', 'Stilt', '1st', '2nd', '3rd', '4th', '5th', '6th', '7th', '8th', '9th', '10th', 'Terrace', 'Roof'];
        const aIdx = order.findIndex(o => a.toLowerCase().includes(o.toLowerCase()));
        const bIdx = order.findIndex(o => b.toLowerCase().includes(o.toLowerCase()));
        if (aIdx !== -1 && bIdx !== -1) return aIdx - bIdx;
        if (aIdx !== -1) return -1;
        if (bIdx !== -1) return 1;
        return a.localeCompare(b);
      })
    };
  }, [tasks]);

  // Filter tasks for display
  const filteredTasks = useMemo(() => {
    return tasks.filter(t => {
      const matchesSearch = !taskSearchQuery || 
        t.taskName?.toLowerCase().includes(taskSearchQuery.toLowerCase()) ||
        t.category?.toLowerCase().includes(taskSearchQuery.toLowerCase()) ||
        t.floor?.toLowerCase().includes(taskSearchQuery.toLowerCase());
      const matchesCategory = !taskFilterCategory || t.category === taskFilterCategory;
      const matchesBuilding = !taskFilterBuilding || t.building === taskFilterBuilding;
      const notCompleted = t.status !== 'Completed';
      return matchesSearch && matchesCategory && matchesBuilding && notCompleted;
    });
  }, [tasks, taskSearchQuery, taskFilterCategory, taskFilterBuilding]);

  // Group tasks by selected grouping option
  const groupedTasks = useMemo(() => {
    const grouped: Record<string, Task[]> = {};
    filteredTasks.forEach(task => {
      let key: string;
      if (groupBy === 'floor') {
        key = task.floor || 'Unknown Floor';
      } else if (groupBy === 'building') {
        key = task.building || 'Unknown Building';
      } else {
        key = task.category || 'Uncategorized';
      }
      if (!grouped[key]) grouped[key] = [];
      grouped[key].push(task);
    });
    
    // Sort groups
    const sortedKeys = Object.keys(grouped).sort((a, b) => {
      if (groupBy === 'floor') {
        const order = ['Plinth', 'Ground', 'Stilt', '1st', '2nd', '3rd', '4th', '5th', '6th', '7th', '8th', '9th', '10th', 'Terrace', 'Roof'];
        const aIdx = order.findIndex(o => a.toLowerCase().includes(o.toLowerCase()));
        const bIdx = order.findIndex(o => b.toLowerCase().includes(o.toLowerCase()));
        if (aIdx !== -1 && bIdx !== -1) return aIdx - bIdx;
        if (aIdx !== -1) return -1;
        if (bIdx !== -1) return 1;
      }
      return a.localeCompare(b);
    });
    
    const sorted: Record<string, Task[]> = {};
    sortedKeys.forEach(key => {
      sorted[key] = grouped[key];
    });
    return sorted;
  }, [filteredTasks, groupBy]);

  // Debounced API call for resource calculation
  useEffect(() => {
    if (selectedTaskIds.size === 0) {
      setResourcePlan(null);
      return;
    }

    const timeoutId = setTimeout(async () => {
      setIsCalculating(true);
      setError(null);
      try {
        const result = await api.calculateResourcePlan(projectId, Array.from(selectedTaskIds));
        setResourcePlan(result);
      } catch (err) {
        console.error('Failed to calculate resource plan:', err);
        setError('Failed to calculate resources. Please try again.');
      } finally {
        setIsCalculating(false);
      }
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [selectedTaskIds, projectId]);

  // Check for over-limit resources
  const weeklyWithLimits = useMemo(() => {
    if (!resourcePlan) return [];
    
    return resourcePlan.weeks.map(week => {
      const overLimit: string[] = [];
      resourceLimits.forEach(limit => {
        if ((week.resources[limit.labor] || 0) > limit.available) {
          overLimit.push(limit.labor);
        }
      });
      return { ...week, overLimit };
    });
  }, [resourcePlan, resourceLimits]);

  // Resource-First: Calculate what tasks can be done with given resources
  const suggestTasksForResources = useCallback(async () => {
    setIsSuggesting(true);
    setError(null);
    try {
      const limits: Record<string, number> = {};
      resourceLimits.forEach(r => {
        limits[r.labor] = r.available;
      });

      const result = await api.suggestTasksForResources(
        projectId, 
        limits, 
        Array.from(selectedTaskIds)
      );
      
      setSuggestedTaskIds(new Set(result.suggested_task_ids.map(id => String(id))));
    } catch (err) {
      console.error('Failed to suggest tasks:', err);
      setError('Failed to suggest tasks. Please try again.');
    } finally {
      setIsSuggesting(false);
    }
  }, [projectId, resourceLimits, selectedTaskIds]);

  // Toggle task selection
  const toggleTask = (taskId: string) => {
    setSelectedTaskIds(prev => {
      const next = new Set(prev);
      if (next.has(taskId)) {
        next.delete(taskId);
      } else {
        next.add(taskId);
      }
      return next;
    });
  };

  // Select all suggested tasks
  const selectAllSuggested = () => {
    setSelectedTaskIds(new Set(suggestedTaskIds));
  };

  // Toggle group expansion
  const toggleGroup = (group: string) => {
    setExpandedGroups(prev => {
      const next = new Set(prev);
      if (next.has(group)) {
        next.delete(group);
      } else {
        next.add(group);
      }
      return next;
    });
  };
  
  // Select all tasks in a group
  const selectAllInGroup = (group: string) => {
    const tasksInGroup = groupedTasks[group] || [];
    setSelectedTaskIds(prev => {
      const next = new Set(prev);
      tasksInGroup.forEach(t => next.add(t.id));
      return next;
    });
  };

  // Update resource limit
  const updateResourceLimit = (labor: string, value: number) => {
    setResourceLimits(prev => 
      prev.map(r => r.labor === labor ? { ...r, available: value } : r)
    );
  };

  // Export to CSV
  const exportPlan = () => {
    if (!resourcePlan) return;

    const rows: string[][] = [];
    rows.push(['Resource Plan Export', projectName, new Date().toLocaleDateString()]);
    rows.push([]);
    rows.push(['Mode', mode === 'task-first' ? 'Task-First Planning' : 'Resource-First Planning']);
    rows.push(['Selected Tasks', resourcePlan.taskCount.toString()]);
    rows.push([]);

    rows.push(['RESOURCE REQUIREMENTS']);
    rows.push(['Labor Type', 'Peak Workers', 'Peak Week', 'Total Worker-Days']);
    Object.entries(resourcePlan.totals).forEach(([labor, data]) => {
      rows.push([labor, data.peak.toString(), `Week ${data.peakWeek}`, data.total.toString()]);
    });
    rows.push([]);

    rows.push(['WEEKLY BREAKDOWN']);
    const laborTypes = Object.keys(resourcePlan.totals);
    rows.push(['Week', 'Start Date', 'End Date', 'Tasks', ...laborTypes]);
    resourcePlan.weeks.forEach(week => {
      rows.push([
        `Week ${week.week}`,
        new Date(week.startDate).toLocaleDateString(),
        new Date(week.endDate).toLocaleDateString(),
        week.taskCount.toString(),
        ...laborTypes.map(l => (week.resources[l] || 0).toString())
      ]);
    });

    const csv = rows.map(row => row.map(cell => `"${cell}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `resource-plan-${projectName.toLowerCase().replace(/\s+/g, '-')}-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl shadow-2xl w-[95vw] max-w-[1400px] h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-violet-50 to-indigo-50 flex-shrink-0">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="p-2 bg-violet-100 rounded-lg">
                <Zap className="w-6 h-6 text-violet-600" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-gray-900">Resource Planner</h2>
                <p className="text-sm text-gray-600">{projectName}</p>
              </div>
            </div>

            {/* Mode Toggle */}
            <div className="flex items-center gap-2 bg-white rounded-lg border border-gray-200 p-1">
              <button
                onClick={() => setMode('task-first')}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                  mode === 'task-first' 
                    ? 'bg-violet-100 text-violet-700' 
                    : 'text-gray-600 hover:bg-gray-50'
                }`}
              >
                <ListChecks className="w-4 h-4" />
                Task-First
              </button>
              <button
                onClick={() => setMode('resource-first')}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                  mode === 'resource-first' 
                    ? 'bg-violet-100 text-violet-700' 
                    : 'text-gray-600 hover:bg-gray-50'
                }`}
              >
                <Users className="w-4 h-4" />
                Resource-First
              </button>
            </div>

            <div className="flex items-center gap-2">
              <button
                onClick={exportPlan}
                disabled={!resourcePlan}
                className="flex items-center gap-2 px-4 py-2 bg-violet-600 text-white rounded-lg hover:bg-violet-700 text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Download className="w-4 h-4" />
                Export
              </button>
              <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg">
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>
          </div>
        </div>

        {/* Mode Description */}
        <div className={`px-6 py-2 border-b ${mode === 'task-first' ? 'bg-blue-50' : 'bg-green-50'}`}>
          {mode === 'task-first' ? (
            <div className="flex items-center gap-3 text-blue-800 text-sm">
              <Target className="w-4 h-4" />
              <span className="font-medium">Task-First:</span>
              <span className="text-blue-600">Select tasks → See resources needed</span>
            </div>
          ) : (
            <div className="flex items-center gap-3 text-green-800 text-sm">
              <Users className="w-4 h-4" />
              <span className="font-medium">Resource-First:</span>
              <span className="text-green-600">Set available workers → See what tasks fit</span>
            </div>
          )}
        </div>

        {/* Error Banner */}
        {error && (
          <div className="px-6 py-2 bg-red-50 border-b border-red-200 flex items-center justify-between">
            <span className="text-red-700 text-sm">{error}</span>
            <button onClick={() => setError(null)} className="text-red-500 hover:text-red-700">
              <X className="w-4 h-4" />
            </button>
          </div>
        )}

        {/* Main Content */}
        <div className="flex-1 flex overflow-hidden">
          {/* Left Panel - Task Selection / Resource Limits */}
          <div className="w-72 border-r border-gray-200 flex flex-col overflow-hidden">
            {mode === 'task-first' ? (
              <>
                {/* Task Search & Filters */}
                <div className="p-3 border-b border-gray-200 space-y-2">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <input
                      type="text"
                      placeholder="Search tasks..."
                      value={taskSearchQuery}
                      onChange={(e) => setTaskSearchQuery(e.target.value)}
                      className="w-full pl-9 pr-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-violet-500 focus:border-violet-500"
                    />
                  </div>
                  
                  {/* Group By Selector */}
                  <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-0.5">
                    <button
                      onClick={() => setGroupBy('floor')}
                      className={`flex-1 px-2 py-1 text-xs font-medium rounded ${
                        groupBy === 'floor' ? 'bg-white text-violet-700 shadow-sm' : 'text-gray-600'
                      }`}
                    >
                      Floor
                    </button>
                    <button
                      onClick={() => setGroupBy('category')}
                      className={`flex-1 px-2 py-1 text-xs font-medium rounded ${
                        groupBy === 'category' ? 'bg-white text-violet-700 shadow-sm' : 'text-gray-600'
                      }`}
                    >
                      Category
                    </button>
                    <button
                      onClick={() => setGroupBy('building')}
                      className={`flex-1 px-2 py-1 text-xs font-medium rounded ${
                        groupBy === 'building' ? 'bg-white text-violet-700 shadow-sm' : 'text-gray-600'
                      }`}
                    >
                      Building
                    </button>
                  </div>

                  <div className="flex gap-2">
                    <select
                      value={taskFilterBuilding}
                      onChange={(e) => setTaskFilterBuilding(e.target.value)}
                      className="flex-1 px-2 py-1 border border-gray-300 rounded text-xs"
                    >
                      <option value="">All Buildings</option>
                      {buildings.map(b => (
                        <option key={b} value={b}>{b}</option>
                      ))}
                    </select>
                    <select
                      value={taskFilterCategory}
                      onChange={(e) => setTaskFilterCategory(e.target.value)}
                      className="flex-1 px-2 py-1 border border-gray-300 rounded text-xs"
                    >
                      <option value="">All Categories</option>
                      {categories.map(c => (
                        <option key={c} value={c}>{c}</option>
                      ))}
                    </select>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-gray-500">
                      {selectedTaskIds.size} selected / {filteredTasks.length} tasks
                    </span>
                    {selectedTaskIds.size > 0 && (
                      <button
                        onClick={() => setSelectedTaskIds(new Set())}
                        className="text-xs text-red-500 hover:text-red-700 font-medium"
                      >
                        Clear
                      </button>
                    )}
                  </div>
                </div>

                {/* Task List */}
                <div className="flex-1 overflow-y-auto">
                  {Object.entries(groupedTasks).map(([group, groupTasks]) => (
                    <div key={group} className="border-b border-gray-100">
                      <div className="flex items-center">
                        <button
                          onClick={() => toggleGroup(group)}
                          className="flex-1 px-3 py-2 flex items-center justify-between hover:bg-gray-50"
                        >
                          <div className="flex items-center gap-2">
                            {expandedGroups.has(group) ? (
                              <ChevronDown className="w-4 h-4 text-gray-400" />
                            ) : (
                              <ChevronRight className="w-4 h-4 text-gray-400" />
                            )}
                            <span className="font-medium text-xs text-gray-700">{group}</span>
                          </div>
                          <span className="text-xs text-gray-500">
                            {groupTasks.filter(t => selectedTaskIds.has(t.id)).length}/{groupTasks.length}
                          </span>
                        </button>
                        <button
                          onClick={() => selectAllInGroup(group)}
                          className="px-2 py-1 mr-2 text-[10px] text-violet-600 hover:text-violet-700 hover:bg-violet-50 rounded font-medium"
                          title={`Select all ${groupTasks.length} tasks`}
                        >
                          All
                        </button>
                      </div>
                      {expandedGroups.has(group) && (
                        <div className="pb-1">
                          {groupTasks.map(task => (
                            <label
                              key={task.id}
                              className="flex items-start gap-2 px-3 py-1 hover:bg-gray-50 cursor-pointer"
                            >
                              <input
                                type="checkbox"
                                checked={selectedTaskIds.has(task.id)}
                                onChange={() => toggleTask(task.id)}
                                className="mt-0.5 rounded border-gray-300 text-violet-600 focus:ring-violet-500"
                              />
                              <div className="flex-1 min-w-0">
                                <div className="text-xs text-gray-800 truncate">{task.taskName || task.subCategory}</div>
                                <div className="text-[10px] text-gray-500">
                                  {groupBy !== 'floor' && task.floor && `${task.floor} • `}
                                  {groupBy !== 'category' && task.category && `${task.category} • `}
                                  {task.quantity} {task.taskUnit}
                                </div>
                              </div>
                            </label>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </>
            ) : (
              <>
                {/* Resource Limits */}
                <div className="p-3 border-b border-gray-200">
                  <h3 className="font-medium text-gray-800 text-sm mb-2">Resources / Week</h3>
                  <div className="space-y-2">
                    {resourceLimits.map(limit => (
                      <div key={limit.labor} className="flex items-center gap-2">
                        <div 
                          className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                          style={{ backgroundColor: limit.color }}
                        />
                        <span className="text-xs text-gray-700 flex-1">{limit.labor}</span>
                        <input
                          type="number"
                          value={limit.available}
                          onChange={(e) => updateResourceLimit(limit.labor, parseInt(e.target.value) || 0)}
                          className="w-14 px-2 py-1 border border-gray-300 rounded text-xs text-center"
                          min="0"
                        />
                      </div>
                    ))}
                  </div>
                </div>

                {/* Calculate Button */}
                <div className="p-3 border-b border-gray-200">
                  <button
                    onClick={suggestTasksForResources}
                    disabled={isSuggesting}
                    className="w-full flex items-center justify-center gap-2 px-3 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm font-medium disabled:opacity-50"
                  >
                    {isSuggesting ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Sparkles className="w-4 h-4" />
                    )}
                    Find Tasks
                  </button>
                  {suggestedTaskIds.size > 0 && (
                    <div className="mt-2 text-center">
                      <p className="text-xs text-gray-600">
                        <span className="font-bold text-green-600">{suggestedTaskIds.size}</span> tasks fit
                      </p>
                      <button
                        onClick={selectAllSuggested}
                        className="text-xs text-violet-600 hover:text-violet-700 font-medium"
                      >
                        Select All
                      </button>
                    </div>
                  )}
                </div>

                {/* Suggested Tasks Preview */}
                <div className="flex-1 overflow-y-auto p-3">
                  <h4 className="text-[10px] font-semibold text-gray-500 uppercase mb-1">
                    Suggested ({suggestedTaskIds.size})
                  </h4>
                  <div className="space-y-1">
                    {tasks.filter(t => suggestedTaskIds.has(t.id)).slice(0, 30).map(task => (
                      <label
                        key={task.id}
                        className="flex items-start gap-2 p-1.5 rounded hover:bg-gray-50 cursor-pointer"
                      >
                        <input
                          type="checkbox"
                          checked={selectedTaskIds.has(task.id)}
                          onChange={() => toggleTask(task.id)}
                          className="mt-0.5 rounded border-gray-300 text-violet-600 focus:ring-violet-500"
                        />
                        <div className="flex-1 min-w-0">
                          <div className="text-[10px] text-gray-800 truncate">{task.category} - {task.subCategory}</div>
                          <div className="text-[10px] text-gray-500">{task.floor}</div>
                        </div>
                      </label>
                    ))}
                  </div>
                </div>
              </>
            )}
          </div>

          {/* Right Panel - Results */}
          <div className="flex-1 flex flex-col overflow-hidden">
            {selectedTaskIds.size === 0 ? (
              <div className="flex-1 flex items-center justify-center">
                <div className="text-center">
                  <BarChart3 className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                  <p className="text-gray-500">
                    {mode === 'task-first' 
                      ? 'Select tasks to see resource requirements' 
                      : 'Set resources and click "Find Tasks"'}
                  </p>
                </div>
              </div>
            ) : isCalculating ? (
              <div className="flex-1 flex items-center justify-center">
                <div className="text-center">
                  <Loader2 className="w-12 h-12 text-violet-500 mx-auto mb-4 animate-spin" />
                  <p className="text-gray-500">Calculating...</p>
                </div>
              </div>
            ) : resourcePlan ? (
              <div className="flex-1 flex flex-col overflow-hidden">
                {/* Summary Row */}
                <div className="px-4 py-3 border-b border-gray-200 bg-gray-50 flex items-center justify-between">
                  <div className="flex items-center gap-6">
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-gray-500">Tasks:</span>
                      <span className="font-bold text-gray-900">{resourcePlan.taskCount}</span>
                    </div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-gray-500">Duration:</span>
                    <span className="font-bold text-gray-900">{resourcePlan.weekCount} weeks</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-gray-500">Peak:</span>
                    <span className="font-bold text-gray-900">
                      {Math.max(...Object.values(resourcePlan.totals).map(r => r.peak), 0)} workers
                    </span>
                  </div>
                  {weeklyWithLimits.some(w => w.overLimit.length > 0) && (
                      <div className="flex items-center gap-2 text-red-600">
                        <AlertTriangle className="w-4 h-4" />
                        <span className="text-xs font-medium">
                          {weeklyWithLimits.filter(w => w.overLimit.length > 0).length} weeks over limit
                        </span>
                      </div>
                    )}
                  </div>
                  <button
                    onClick={() => {
                      setSelectedTaskIds(new Set());
                      setSuggestedTaskIds(new Set());
                    }}
                    className="px-3 py-1 text-xs text-red-600 hover:text-red-700 hover:bg-red-50 rounded font-medium border border-red-200"
                  >
                    Clear All
                  </button>
                </div>

                {/* Resource Legend */}
                <div className="px-4 py-2 border-b border-gray-100 flex flex-wrap gap-3">
                  {Object.entries(resourcePlan.totals).map(([labor, data]) => {
                    const limit = resourceLimits.find(r => r.labor === labor)?.available || Infinity;
                    const isOverLimit = data.peak > limit;
                    return (
                      <div key={labor} className="flex items-center gap-1.5">
                        <div 
                          className="w-3 h-3 rounded-full"
                          style={{ backgroundColor: getLaborColor(labor) }}
                        />
                        <span className="text-xs text-gray-700">{labor}</span>
                        <span className={`text-xs ${isOverLimit ? 'text-red-600 font-bold' : 'text-gray-500'}`}>
                          (peak: {data.peak})
                        </span>
                      </div>
                    );
                  })}
                </div>

                {/* Line Chart */}
                <div className="px-4 py-3 border-b border-gray-200 bg-white">
                  <div className="flex items-center gap-2 mb-2">
                    <TrendingUp className="w-4 h-4 text-gray-400" />
                    <h3 className="text-xs font-semibold text-gray-700">Resource Trend</h3>
                  </div>
                  <ResourceLineChart 
                    weeks={resourcePlan.weeks} 
                    totals={resourcePlan.totals}
                    resourceLimits={resourceLimits}
                  />
                </div>

                {/* Weekly Calendar Grid */}
                <div className="flex-1 overflow-auto p-4">
                  <h3 className="text-xs font-semibold text-gray-700 mb-2">Weekly Calendar</h3>
                  <WeeklyCalendarGrid 
                    weeks={weeklyWithLimits}
                    totals={resourcePlan.totals}
                    resourceLimits={resourceLimits}
                  />
                </div>

                {/* Action Footer */}
                {onCreatePlan && (
                  <div className="p-3 border-t border-gray-200 bg-gray-50 flex justify-end gap-3">
                    <button
                      onClick={() => onCreatePlan(tasks.filter(t => selectedTaskIds.has(t.id)), 'monthly')}
                      className="px-4 py-2 bg-violet-600 text-white rounded-lg hover:bg-violet-700 text-sm font-medium"
                    >
                      Create Monthly Plan
                    </button>
                  </div>
                )}
              </div>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResourcePlannerModal;
